/**
 * Symbol List Store
 *
 * Manages a preloaded database of all US exchange symbols (~8,000 stocks)
 * for instant client-side autocomplete search.
 *
 * Architecture:
 * 1. On app init, check localStorage for cached symbols
 * 2. If stale (>24h) or missing, fetch from /symbols-list API
 * 3. Store in memory + localStorage for instant search
 * 4. Search happens locally - no network latency
 */

import { create } from 'zustand';
import { gatewayFetch } from '../services/gateway';
import { getErrorMessage } from '../utils/validation';

export interface SymbolEntry {
  symbol: string;
  name: string;
  exchange: string;
  type?: string;
}

interface SymbolListState {
  symbols: SymbolEntry[];
  isLoaded: boolean;
  isLoading: boolean;
  lastUpdated: string | null;
  error: string | null;
  loadSymbols: () => Promise<void>;
  searchSymbols: (query: string) => SymbolEntry[];
}

const CACHE_KEY = 'indexalpha_symbol_list';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in ms

/**
 * Fast local search implementation
 * Prioritizes exact symbol matches, then prefix matches, then name contains
 */
function localSearch(symbols: SymbolEntry[], query: string, limit: number = 10): SymbolEntry[] {
  if (!query || query.length < 1) return [];

  const upperQuery = query.toUpperCase();
  const lowerQuery = query.toLowerCase();

  // Three-tier ranking:
  // 1. Exact symbol match (highest priority)
  // 2. Symbol starts with query
  // 3. Name contains query

  const exactMatches: SymbolEntry[] = [];
  const prefixMatches: SymbolEntry[] = [];
  const nameMatches: SymbolEntry[] = [];

  for (const s of symbols) {
    const upperSymbol = s.symbol.toUpperCase();
    const lowerName = s.name.toLowerCase();

    if (upperSymbol === upperQuery) {
      exactMatches.push(s);
    } else if (upperSymbol.startsWith(upperQuery)) {
      prefixMatches.push(s);
    } else if (lowerName.includes(lowerQuery)) {
      nameMatches.push(s);
    }

    // Early exit if we have enough results
    if (exactMatches.length + prefixMatches.length + nameMatches.length >= limit * 2) {
      break;
    }
  }

  // Combine results with priority order
  return [...exactMatches, ...prefixMatches, ...nameMatches].slice(0, limit);
}

export const useSymbolListStore = create<SymbolListState>((set, get) => ({
  symbols: [],
  isLoaded: false,
  isLoading: false,
  lastUpdated: null,
  error: null,

  loadSymbols: async () => {
    // Prevent duplicate loading
    if (get().isLoading || get().isLoaded) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      // Check localStorage cache first
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const { symbols, lastUpdated, timestamp } = JSON.parse(cached);

          // Use cache if not stale
          if (Date.now() - timestamp < CACHE_TTL && symbols && symbols.length > 0) {
            set({
              symbols,
              lastUpdated,
              isLoaded: true,
              isLoading: false
            });
            return;
          }
        } catch {
          localStorage.removeItem(CACHE_KEY);
        }
      }

      // Fetch from API
      const response = await gatewayFetch('fmp', 'symbols-list', { auth: 'none' });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.symbols && data.symbols.length > 0) {
        set({
          symbols: data.symbols,
          lastUpdated: data.lastUpdated,
          isLoaded: true,
          isLoading: false
        });

        // Cache in localStorage
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            symbols: data.symbols,
            lastUpdated: data.lastUpdated,
            timestamp: Date.now()
          }));
        } catch {
          // Quota exceeded — cache will be skipped, symbols still loaded in memory
        }
      } else if (data.error) {
        throw new Error(data.error);
      } else {
        set({
          isLoaded: true,
          isLoading: false,
          error: 'No symbols available'
        });
      }

    } catch (error) {
      set({
        isLoaded: true,
        isLoading: false,
        error: getErrorMessage(error, 'Failed to load symbols')
      });
    }
  },

  searchSymbols: (query: string): SymbolEntry[] => {
    const { symbols } = get();
    return localSearch(symbols, query, 10);
  }
}));

/**
 * Initialize symbol list on module load
 * This allows the list to start loading before any component mounts
 */
export function initializeSymbolList(): void {
  // Delay slightly to not block initial render
  setTimeout(async () => {
    await useSymbolListStore.getState().loadSymbols();
    // Retry once after 10s if initial load failed
    const { isLoaded } = useSymbolListStore.getState();
    if (!isLoaded) {
      setTimeout(() => useSymbolListStore.getState().loadSymbols(), 10_000);
    }
  }, 100);
}
